import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, MessageCircle, Printer, XCircle } from "lucide-react";
import { api, openDownload, shopDateTime } from "@/lib/api";
import { AuthedImage } from "@/components/AuthedImage";
import { openIdRequest } from "@/lib/idRequest";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const FIELDS = [
  ["Name as per passport", "name_as_per_passport"],
  ["Nationality", "nationality"],
  ["Driver licence no", "licence_number"],
  ["Licence state", "licence_state"],
  ["Licence expiry", "licence_expiry"],
  ["Passport number", "passport_number"],
  ["Passport issue date", "passport_issue"],
  ["Passport expiry", "passport_expiry"],
  ["Company name", "company_name"],
  ["ABN", "abn"],
  ["ACN", "acn"],
  ["Address", "address"],
  ["Email", "email"],
  ["WhatsApp mobile", "whatsapp_mobile"],
];

export default function KycView() {
  const { id } = useParams();
  const [rec, setRec] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/kyc/${id}`)
      .then(({ data }) => setRec(data))
      .catch(() => toast.error("Could not load that KYC form"));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/kyc/${id}/review`, { status, reason });
      setRec(data);
      toast.success(status === "verified" ? "Marked as verified" : "Marked as rejected");
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not save that decision"
      );
    } finally {
      setBusy(false);
    }
  };

  const printPdf = async () => {
    try {
      const res = await api.get(`/kyc/${id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) {
      toast.error("Could not build the PDF");
    }
  };

  if (!rec) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="fade-up max-w-4xl" data-testid="kyc-view">
      <Link
        to="/kyc"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground transition-colors duration-150 hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All KYC forms
      </Link>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">{rec.customer_name}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {rec.branch} · submitted {shopDateTime(rec.submitted_at)}
            {rec.emailed_to ? ` · copy emailed to ${rec.emailed_to}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`px-3 py-1.5 text-xs font-bold uppercase ${
              rec.status === "verified"
                ? "bg-[#166534] text-white"
                : rec.status === "rejected"
                  ? "bg-[#9A3412] text-white"
                  : "bg-[#C5A059] text-[#0A0A0A]"
            }`}
            data-testid="kyc-view-status"
          >
            {rec.status}
          </span>
          {rec.id_expired && (
            <span
              className="border border-[#9A3412] px-3 py-1.5 text-xs font-semibold text-[#9A3412]"
              data-testid="kyc-id-expired"
            >
              ID expired
            </span>
          )}
          <Button variant="outline" className="rounded-none" onClick={printPdf} data-testid="kyc-print-button">
            <Printer className="mr-1.5 h-4 w-4" /> KYC PDF
          </Button>
        </div>
      </div>

      <div className="mt-6 border border-border p-5">
        <h2 className="text-base font-bold md:text-lg">Details given by the customer</h2>
        <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {FIELDS.map(([label, key]) => (
            <div key={key} className="flex gap-2 text-sm">
              <dt className="w-[170px] text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
              </dt>
              <dd className="flex-1" data-testid={`kyc-field-${key}`}>
                {rec[key] || "-"}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-4 text-sm">
          <span className="font-semibold">Declaration accepted:</span>{" "}
          {rec.declaration_accepted ? "Yes" : "No"}
          {" · privacy notice: "}
          {rec.privacy_accepted ? "accepted" : "not recorded"}
        </p>
      </div>

      <div className="mt-5 border border-border p-5">
        <h2 className="text-base font-bold md:text-lg">ID documents &amp; signature</h2>
        <div className="mt-3 flex flex-wrap gap-3">
          {(rec.attachments || []).map((a, i) => (
            <button
              type="button"
              key={`${a.file_id}-${i}`}
              onClick={() =>
                openDownload(`/files/${a.file_id}`).catch(() =>
                  toast.error("Could not open that file")
                )
              }
              className="w-[150px] border border-border text-left transition-colors duration-150 hover:border-[#C5A059]"
              data-testid={`kyc-attachment-${i}`}
            >
              {a.content_type === "application/pdf" ? (
                <div className="flex h-[110px] items-center justify-center bg-muted text-xs">
                  PDF document
                </div>
              ) : (
                <AuthedImage fileId={a.file_id} alt="ID document" className="h-[110px] w-full object-cover" />
              )}
              <div className="px-2 py-1 text-[10px] text-muted-foreground">
                {a.size ? `${(a.size / 1024).toFixed(0)} KB` : a.kind}
              </div>
            </button>
          ))}
          {rec.signature_file_id && (
            <div className="w-[190px] border border-border" data-testid="kyc-signature">
              <AuthedImage
                fileId={rec.signature_file_id}
                alt="Customer signature"
                className="h-[110px] w-full object-contain"
              />
              <div className="px-2 py-1 text-[10px] text-muted-foreground">Signature</div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 border border-border p-5">
        <h2 className="text-base font-bold md:text-lg">Your check</h2>
        {rec.reviewed_by ? (
          <p className="mt-2 text-sm">
            {rec.status === "verified" ? "Verified" : "Rejected"} by {rec.reviewed_by} ·{" "}
            {shopDateTime(rec.reviewed_at)}
            {rec.review_reason ? ` — ${rec.review_reason}` : ""}
          </p>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">Not checked yet.</p>
        )}
        <Input
          className="mt-3 h-10 rounded-none"
          placeholder="Reason (required if rejecting)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="kyc-reason-input"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {rec.status !== "verified" && (
            <Button
              className="rounded-none bg-[#166534] font-semibold text-white hover:bg-[#14532D]"
              onClick={() => review("verified")}
              disabled={busy}
              data-testid="kyc-verify-button"
            >
              <BadgeCheck className="mr-1.5 h-4 w-4" /> Mark verified
            </Button>
          )}
          <Button
            variant="outline"
            className="rounded-none text-[#9A3412]"
            onClick={() => review("rejected")}
            disabled={busy}
            data-testid="kyc-reject-button"
          >
            <XCircle className="mr-1.5 h-4 w-4" /> Reject
          </Button>
          {rec.status === "rejected" && (
            <Button
              variant="outline"
              className="rounded-none border-[#1F6F43] text-[#1F6F43]"
              onClick={() =>
                openIdRequest({
                  phone: rec.whatsapp_mobile,
                  customer_name: rec.customer_name,
                  branch: rec.branch,
                  reason: rec.review_reason,
                  kyc_id: rec.id,
                })
              }
              data-testid="kyc-ask-again-button"
            >
              <MessageCircle className="mr-1.5 h-4 w-4" /> Ask again on WhatsApp
            </Button>
          )}
        </div>
        {rec.status === "rejected" && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="kyc-ask-again-hint">
            The message tells the customer why it was not accepted and links them to a fresh
            form. They stay on the "Waiting on customer" list until a new form is verified.
          </p>
        )}
      </div>
    </div>
  );
}
